import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from "recharts";
import { reportService } from "../../services/reportService";
import { invoiceService } from "../../services/invoiceService";
import Icon from "../../components/Common/Icon";
import { useToast } from "../../context/ToastContext";
import { useChartColors } from "../../theme/ThemeContext";

const fmtTL = (n) =>
  (n == null ? 0 : Number(n)).toLocaleString("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0,
  });
const fmtNum = (n) => (n == null ? 0 : Number(n)).toLocaleString("tr-TR");

const monthLabel = (m) => {
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("tr-TR", { month: "short", year: "2-digit" });
};

// Fatura kova tarihini (YYYY-MM-DD) seçilen kırılıma göre etiketle.
const invBucketLabel = (d, g) => {
  if (!d) return "";
  const [y, m, day] = d.split("-");
  const dt = new Date(Number(y), Number(m) - 1, Number(day));
  switch (g) {
    case "DAY":
      return dt.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" });
    case "WEEK":
      return dt.toLocaleDateString("tr-TR", { day: "2-digit", month: "short" }) + " haftası";
    case "YEAR":
      return y;
    default:
      return dt.toLocaleDateString("tr-TR", { month: "short", year: "numeric" });
  }
};

const monthBounds = () => {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
};

const ACCENTS = {
  success: "bg-success/10 text-success",
  error: "bg-error/10 text-error",
  primary: "bg-primary/10 text-primary",
  gold: "bg-[#fbbf24]/15 text-[#b45309]",
};

function StatCard({ label, value, icon, accent = "primary", valueClass, money }) {
  return (
    <div className="bg-surface-card rounded-2xl border border-outline-variant p-4 flex items-center gap-3">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${ACCENTS[accent]}`}>
        <Icon name={icon} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-on-surface-variant truncate">{label}</p>
        <p className={`text-xl font-bold ${valueClass ?? "text-on-surface"}`}>
          {money ? fmtTL(value) : fmtNum(value)}
        </p>
      </div>
    </div>
  );
}

// CSV üret + indir (Excel için BOM'lu)
function downloadCsv(filename, rows) {
  const csv = rows
    .map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";"))
    .join("\r\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function ReportsPage() {
  const user = useSelector((state) => state.auth.user);
  if (user?.role === "ADMIN") return <PlatformReport />;
  return <SalonReport />;
}

/* ------------------------------------------------------------------ MANAGER */

function SalonReport() {
  const user = useSelector((state) => state.auth.user);
  const { showToast } = useToast();
  const C = useChartColors();
  const [range, setRange] = useState(monthBounds());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("ozet");

  // Fatura raporu ayrı endpoint + kırılım seçici ile beslenir (yalnız sekme aktifken).
  const [invGranularity, setInvGranularity] = useState("MONTH");
  const [invData, setInvData] = useState(null);
  const [invLoading, setInvLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    reportService
      .summary(range.from, range.to)
      .then((res) => setData(res.data?.data ?? null))
      .catch(() => showToast("Rapor yüklenemedi.", "error"))
      .finally(() => setLoading(false));
  }, [range.from, range.to]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab !== "fatura") return;
    setInvLoading(true);
    invoiceService
      .reportSummary(range.from, range.to, invGranularity)
      .then((res) => setInvData(res.data?.data ?? null))
      .catch(() => showToast("Fatura raporu yüklenemedi.", "error"))
      .finally(() => setInvLoading(false));
  }, [activeTab, range.from, range.to, invGranularity]); // eslint-disable-line react-hooks/exhaustive-deps

  const trendData = useMemo(
    () =>
      (data?.monthlyTrend ?? []).map((p) => ({
        name: monthLabel(p.month),
        Gelir: Number(p.income || 0),
        Gider: Number(p.expense || 0),
      })),
    [data]
  );

  const exportCsv = () => {
    if (!data) return;
    const rows = [
      ["Rapor Dönemi", `${data.from} - ${data.to}`],
      ["Paket", data.packageName],
      [],
      ["Özet", "Değer"],
      ["Toplam Gelir", data.totalIncome],
      ["Toplam Gider", data.totalExpense],
      ["Net Kâr/Zarar", data.net],
      ["Toplam Randevu", data.totalAppointments],
      ["Tamamlanan", data.completedAppointments],
      ["İptal", data.cancelledAppointments],
      ["Bekleyen", data.scheduledAppointments],
      ["Yeni Müşteri", data.newCustomers],
      ["Toplam Müşteri", data.totalCustomers],
      ["Aktif Personel", data.activeStaff],
    ];

    if ((data.paymentMethods ?? []).length) {
      rows.push([], ["Ödeme Yöntemi Dağılımı", "Tutar"]);
      data.paymentMethods.forEach((m) => rows.push([m.category, m.amount]));
    }
    if (data.discountCampaignCost) {
      rows.push([], ["İndirim ve Kampanya Maliyeti", "Tutar"]);
      rows.push(["Kampanya İndirimi", data.discountCampaignCost.campaignDiscount]);
      rows.push(["Sadakat İndirimi", data.discountCampaignCost.loyaltyDiscount]);
      rows.push(["Toplam İndirim", data.discountCampaignCost.totalDiscount]);
    }
    if ((data.cashDiffs ?? []).length) {
      rows.push([], ["Kasa Sayım Farkları", "Tarih", "Beklenen", "Sayılan", "Fark"]);
      data.cashDiffs.forEach((d) => rows.push([d.date, d.expectedAmount, d.actualAmount, d.difference]));
    }
    if ((data.profitMargins ?? []).length) {
      rows.push([], ["Kâr Marjı Analizi", "Ad", "Tip", "Gelir", "Maliyet", "Net Kâr", "Marj (%)"]);
      data.profitMargins.forEach((m) => rows.push([m.name, m.type, m.revenue, m.cost, m.profit, m.margin]));
    }
    if ((data.topServices ?? []).length) {
      rows.push([], ["En Çok Kazandıran Hizmetler", "Adet", "Ciro"]);
      data.topServices.forEach((s) => rows.push([s.name, s.count, s.revenue]));
    }
    if ((data.avgDurations ?? []).length) {
      rows.push([], ["Ortalama Hizmet Süreleri", "Hizmet", "Süre (Dakika)"]);
      data.avgDurations.forEach((d) => rows.push([d.name, d.avgDurationMinutes]));
    }
    if ((data.noShows ?? []).length) {
      rows.push([], ["No-Show Analizi", "Tarih", "Toplam", "İptal", "İptal Oranı (%)"]);
      data.noShows.forEach((n) => rows.push([n.date, n.totalCount, n.cancelledCount, n.cancellationRate]));
    }
    if (data.customerRetention) {
      rows.push([], ["Müşteri Retention", "Toplam Müşteri", "Aktif Müşteri", "Tekrar Gelen", "Retention Oranı (%)"]);
      rows.push([
        data.customerRetention.totalCustomers,
        data.customerRetention.activeInPeriod,
        data.customerRetention.repeatCustomers,
        data.customerRetention.retentionRate,
      ]);
    }
    if ((data.servicePreferences ?? []).length) {
      rows.push([], ["Hizmet Tercihleri", "Hizmet", "Tekil Müşteri", "Toplam Tercih"]);
      data.servicePreferences.forEach((p) => rows.push([p.name, p.customerCount, p.bookingCount]));
    }
    if ((data.topStaff ?? []).length) {
      rows.push([], ["Personel Performansı", "Randevu", "Ciro"]);
      data.topStaff.forEach((s) => rows.push([s.name, s.appointments, s.revenue]));
    }
    if ((data.staffCommissions ?? []).length) {
      rows.push([], ["Komisyon & Prim Raporu", "Personel", "Taban Maaş", "Hakedilen Prim", "Ek Prim/Bonus", "Net Ödeme"]);
      data.staffCommissions.forEach((c) => rows.push([c.name, c.baseSalary, c.commission, c.bonus, c.netPay]));
    }
    if ((data.criticalStocks ?? []).length) {
      rows.push([], ["Kritik Stok Seviyeleri", "Ürün", "Mevcut", "Kritik Eşik", "Birim"]);
      data.criticalStocks.forEach((s) => rows.push([s.name, s.quantity, s.minQuantity, s.unit]));
    }
    if ((data.productSales ?? []).length) {
      rows.push([], ["Ürün Satış Performansı", "Ürün", "Adet", "Gelir", "Maliyet", "Net Kâr"]);
      data.productSales.forEach((s) => rows.push([s.name, s.quantitySold, s.revenue, s.cost, s.profit]));
    }
    if ((data.serviceConsumption ?? []).length) {
      rows.push([], ["Hizmet Başına Ürün Tüketimi", "Hizmet", "Ürün", "Miktar", "Birim", "Maliyet"]);
      data.serviceConsumption.forEach((s) =>
        rows.push([s.serviceName || "Hizmetsiz", s.productName, s.quantity, s.unit, s.cost])
      );
    }
    if (data.stockCost) {
      rows.push([], ["Stok Maliyet Analizi", "Değer"]);
      rows.push(["Anlık Stok Değeri", data.stockCost.currentStockValue]);
      rows.push(["Alım Maliyeti", data.stockCost.purchaseCost]);
      rows.push(["Hizmet Tüketim Maliyeti", data.stockCost.consumptionCost]);
      rows.push(["Fire Maliyeti", data.stockCost.wasteCost]);
      rows.push(["Ürün Satış Maliyeti", data.stockCost.salesCost]);
      rows.push(["Ürün Satış Cirosu", data.stockCost.salesRevenue]);
      rows.push(["Ürün Satış Kârı", data.stockCost.salesProfit]);
    }

    downloadCsv(`rapor_${data.from}_${data.to}.csv`, rows);
  };

  const net = Number(data?.net || 0);
  const feats = data?.features ?? [];
  const has = (k) => feats.some((f) => f.key === k);
  const CONTROLS = ["REPORT_EXPORT", "REPORT_UNLIMITED_RANGE"];
  const bodyFeats = feats.filter((f) => !CONTROLS.includes(f.key));
  const anyDetail = bodyFeats.length > 0;
  const planned = bodyFeats.filter((f) => !f.implemented);
  const rangeMin = has("REPORT_UNLIMITED_RANGE")
    ? undefined
    : new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10);

  // Tab configurations based on active package features and user roles
  const tabs = useMemo(() => {
    const isStaff = user?.role === "STAFF";
    return [
      { id: "ozet", label: "Genel Özet", icon: "dashboard", visible: !isStaff },
      {
        id: "finans",
        label: "Finans Raporları",
        icon: "payments",
        visible:
          !isStaff &&
          (has("REPORT_CATEGORY_BREAKDOWN") ||
            has("REPORT_PAYMENT_METHODS") ||
            has("REPORT_DISCOUNT_CAMPAIGN") ||
            has("REPORT_CASH_DIFF") ||
            has("REPORT_PROFIT_MARGIN")),
      },
      {
        id: "randevu",
        label: "Randevu Raporları",
        icon: "event",
        visible:
          (!isStaff && has("REPORT_SERVICE_REVENUE")) ||
          has("REPORT_PEAK_HOURS") ||
          has("REPORT_AVG_DURATION") ||
          has("REPORT_NOSHOW_ANALYSIS"),
      },
      {
        id: "musteri",
        label: "Müşteri Raporları",
        icon: "group",
        visible: has("REPORT_CUSTOMER_RETENTION") || has("REPORT_SERVICE_PREFERENCE"),
      },
      {
        id: "personel",
        label: "Personel Raporları",
        icon: "badge",
        visible: !isStaff && (has("REPORT_STAFF_PERFORMANCE") || has("REPORT_COMMISSION")),
      },
      {
        id: "stok",
        label: "Stok & Ürün",
        icon: "inventory_2",
        visible:
          has("REPORT_CRITICAL_STOCK") ||
          has("REPORT_PRODUCT_SALES") ||
          has("REPORT_SERVICE_CONSUMPTION") ||
          has("REPORT_STOCK_COST"),
      },
      {
        id: "fatura",
        label: "Fatura Raporları",
        icon: "receipt_long",
        visible: !isStaff && has("REPORT_INVOICE_SUMMARY"),
      },
    ].filter((t) => t.visible);
  }, [feats, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset active tab if it's hidden under current package or user role
  useEffect(() => {
    if (data && !tabs.some((t) => t.id === activeTab)) {
      setActiveTab(tabs[0]?.id || "ozet");
    }
  }, [tabs, activeTab, data]);

  return (
    <div className="space-y-6">
      {/* Başlık + aksiyonlar */}
      <div className="flex flex-wrap items-center justify-between gap-4 print:hidden">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
            <Icon name="assessment" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-on-surface">Raporlar</h1>
            <p className="text-on-surface-variant text-sm">Dönemsel performans ve gelir analizi.</p>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Başlangıç</label>
            <input type="date" value={range.from} min={rangeMin} onChange={(e) => setRange({ ...range, from: e.target.value })}
              className="border border-outline-variant rounded-xl px-3 py-2 text-sm bg-white outline-none focus:border-primary" />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Bitiş</label>
            <input type="date" value={range.to} min={rangeMin} onChange={(e) => setRange({ ...range, to: e.target.value })}
              className="border border-outline-variant rounded-xl px-3 py-2 text-sm bg-white outline-none focus:border-primary" />
          </div>
          {has("REPORT_EXPORT") && (
            <>
              <button onClick={exportCsv} disabled={!data}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium bg-primary text-white hover:opacity-90 transition disabled:opacity-40">
                <Icon name="download" className="!text-[18px]" /> CSV
              </button>
              <button onClick={() => window.print()} disabled={!data}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium border border-outline-variant text-on-surface hover:bg-surface-container-high transition disabled:opacity-40">
                <Icon name="print" className="!text-[18px]" /> Yazdır
              </button>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div className="bg-surface-card rounded-2xl p-10 border border-outline-variant text-center text-on-surface-variant">Yükleniyor…</div>
      ) : !data ? (
        <div className="bg-surface-card rounded-2xl p-10 border border-outline-variant text-center text-on-surface-variant">Veri yok.</div>
      ) : (
        <>
          {/* Dönem + paket rozeti */}
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-on-surface-variant">Dönem: <b className="text-on-surface">{data.from} → {data.to}</b></span>
            <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${anyDetail ? "bg-[#fbbf24]/20 text-[#b45309]" : "bg-surface-container-high text-on-surface-variant"}`}>
              {data.packageName} Paket
            </span>
          </div>

          {/* Rapor Kategori Tabları */}
          {anyDetail && (
            <div className="flex border-b border-outline-variant overflow-x-auto gap-2 pb-px print:hidden">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                    activeTab === tab.id
                      ? "border-primary text-primary"
                      : "border-transparent text-on-surface-variant hover:text-on-surface"
                  }`}
                >
                  <Icon name={tab.icon} className="!text-[18px]" />
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          {/* TAB 1: Genel Özet */}
          {activeTab === "ozet" && user?.role !== "STAFF" && (
            <div className="space-y-6">
              {/* Otomatik rapor e-postası ayarı */}
              {has("REPORT_AUTO_EMAIL") && <AutoReportEmailCard />}

              {/* Finans kartları */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <StatCard money label="Toplam Gelir" value={data.totalIncome} icon="trending_up" accent="success" valueClass="text-success" />
                <StatCard money label="Toplam Gider" value={data.totalExpense} icon="trending_down" accent="error" valueClass="text-error" />
                <StatCard money label="Net Kâr / Zarar" value={net} icon={net >= 0 ? "savings" : "warning"}
                  accent={net >= 0 ? "primary" : "error"} valueClass={net >= 0 ? "text-primary" : "text-error"} />
              </div>

              {/* Operasyon kartları */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <StatCard label="Randevu" value={data.totalAppointments} icon="event" />
                <StatCard label="Tamamlanan" value={data.completedAppointments} icon="event_available" accent="success" valueClass="text-success" />
                <StatCard label="İptal" value={data.cancelledAppointments} icon="event_busy" accent="error" valueClass="text-error" />
                <StatCard label="Yeni Müşteri" value={data.newCustomers} icon="person_add" />
                <StatCard label="Toplam Müşteri" value={data.totalCustomers} icon="group" />
                <StatCard label="Aktif Personel" value={data.activeStaff} icon="badge" />
              </div>

              {!anyDetail ? (
                <UpsellBanner />
              ) : (
                <>
                  {/* Aylık trend */}
                  {has("REPORT_MONTHLY_TREND") && (
                    <div className="bg-surface-card rounded-2xl border border-outline-variant p-4">
                      <h3 className="text-sm font-bold text-on-surface mb-3">Aylık Gelir / Gider</h3>
                      {trendData.length === 0 ? (
                        <div className="h-[240px] flex items-center justify-center text-sm text-on-surface-variant">Veri yok</div>
                      ) : (
                        <ResponsiveContainer width="100%" height={240}>
                          <BarChart data={trendData} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
                            <XAxis dataKey="name" tick={{ fontSize: 12, fill: C.muted }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fontSize: 11, fill: C.muted }} axisLine={false} tickLine={false} width={60}
                                   tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
                            <Tooltip formatter={(v) => fmtTL(v)} cursor={{ fill: "rgba(124,58,237,0.06)" }} />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Bar dataKey="Gelir" fill={C.success} radius={[6, 6, 0, 0]} />
                            <Bar dataKey="Gider" fill={C.error} radius={[6, 6, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                  )}

                  {/* Yakında — verisi/modülü henüz geliştirilmemiş raporlar */}
                  {planned.length > 0 && (
                    <div className="space-y-3">
                      <h3 className="text-sm font-bold text-on-surface flex items-center gap-2">
                        <Icon name="schedule" className="!text-[18px] text-primary" /> Diğer Paket Özellikleri
                        <span className="text-xs font-normal text-on-surface-variant">
                          (paketinize dahil, ek özellikler)
                        </span>
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {planned.map((f) => (
                          <PlannedCard key={f.key} feature={f} />
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* TAB 2: Finans Raporları */}
          {activeTab === "finans" && user?.role !== "STAFF" && (
            <div className="space-y-6">
              {/* Kampanya / İndirim Kartları */}
              {has("REPORT_DISCOUNT_CAMPAIGN") && data.discountCampaignCost && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="bg-surface-card rounded-2xl border border-outline-variant p-4">
                    <p className="text-xs text-on-surface-variant">Kampanya İndirim Maliyeti</p>
                    <p className="text-xl font-bold text-error mt-1">{fmtTL(data.discountCampaignCost.campaignDiscount)}</p>
                  </div>
                  <div className="bg-surface-card rounded-2xl border border-outline-variant p-4">
                    <p className="text-xs text-on-surface-variant">Sadakat Puan İndirim Maliyeti</p>
                    <p className="text-xl font-bold text-error mt-1">{fmtTL(data.discountCampaignCost.loyaltyDiscount)}</p>
                  </div>
                  <div className="bg-surface-card rounded-2xl border border-outline-variant p-4 bg-error/5">
                    <p className="text-xs text-on-surface-variant">Toplam İndirim & Promosyon Maliyeti</p>
                    <p className="text-xl font-bold text-error mt-1">{fmtTL(data.discountCampaignCost.totalDiscount)}</p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {/* Kategori kırılımları */}
                {has("REPORT_CATEGORY_BREAKDOWN") && (
                  <>
                    <CategoryCard title="Gelir Kategorileri" rows={data.incomeByCategory} />
                    <CategoryCard title="Gider Kategorileri" rows={data.expenseByCategory} />
                  </>
                )}

                {/* Ödeme yöntemi dağılımı */}
                {has("REPORT_PAYMENT_METHODS") && (
                  <CategoryCard title="Ödeme Yöntemi Dağılımı" rows={data.paymentMethods} />
                )}
              </div>

              {/* Kasa sayım farkları */}
              {has("REPORT_CASH_DIFF") && (
                <div className="bg-surface-card rounded-2xl border border-outline-variant p-4">
                  <h3 className="text-sm font-bold text-on-surface mb-3 flex items-center gap-2">
                    <Icon name="savings" className="!text-[18px] text-primary" /> Kasa Açılış / Kapanış Farkları
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-xs text-on-surface-variant border-b border-outline-variant text-left">
                          <th className="py-2 font-semibold">Tarih</th>
                          <th className="py-2 font-semibold text-right">Sistem Kapanış Tutar (Beklenen)</th>
                          <th className="py-2 font-semibold text-right">Physical Sayılan (Actual)</th>
                          <th className="py-2 font-semibold text-right">Sayım Farkı (Variance)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data.cashDiffs ?? []).map((row, idx) => (
                          <tr key={idx} className="border-b border-outline-variant/50 last:border-0">
                            <td className="py-2.5 text-on-surface font-medium">{row.date}</td>
                            <td className="py-2.5 text-right text-on-surface-variant">{fmtTL(row.expectedAmount)}</td>
                            <td className="py-2.5 text-right font-semibold text-on-surface">{fmtTL(row.actualAmount)}</td>
                            <td className={`py-2.5 text-right font-bold ${
                              Number(row.difference) === 0
                                ? "text-success"
                                : Number(row.difference) > 0
                                ? "text-primary"
                                : "text-error"
                            }`}>
                              {Number(row.difference) > 0 ? "+" : ""}
                              {fmtTL(row.difference)}
                              {Number(row.difference) !== 0 && (
                                <span className="text-[10px] ml-1 font-normal opacity-85">
                                  ({Number(row.difference) > 0 ? "Fazla" : "Açık"})
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Kâr marjı analizi */}
              {has("REPORT_PROFIT_MARGIN") && (
                <div className="bg-surface-card rounded-2xl border border-outline-variant p-4">
                  <h3 className="text-sm font-bold text-on-surface mb-3 flex items-center gap-2">
                    <Icon name="analytics" className="!text-[18px] text-primary" /> Kâr Marjı Analizi (Hizmet & Ürün)
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-xs text-on-surface-variant border-b border-outline-variant text-left">
                          <th className="py-2 font-semibold">Ad</th>
                          <th className="py-2 font-semibold">Tip</th>
                          <th className="py-2 font-semibold text-right">Ciro (Gelir)</th>
                          <th className="py-2 font-semibold text-right">Tahmini Maliyet</th>
                          <th className="py-2 font-semibold text-right">Net Kâr</th>
                          <th className="py-2 font-semibold text-right">Kâr Marjı (%)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data.profitMargins ?? []).map((row, idx) => (
                          <tr key={idx} className="border-b border-outline-variant/50 last:border-0">
                            <td className="py-2.5 text-on-surface font-medium">{row.name}</td>
                            <td className="py-2.5">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                row.type === "Hizmet" ? "bg-primary/10 text-primary" : "bg-[#fbbf24]/10 text-[#b45309]"
                              }`}>
                                {row.type}
                              </span>
                            </td>
                            <td className="py-2.5 text-right text-on-surface">{fmtTL(row.revenue)}</td>
                            <td className="py-2.5 text-right text-on-surface-variant">{fmtTL(row.cost)}</td>
                            <td className="py-2.5 text-right text-success font-semibold">{fmtTL(row.profit)}</td>
                            <td className="py-2.5 text-right font-bold">
                              <div className="flex items-center justify-end gap-1.5">
                                <span>%{row.margin}</span>
                                <div className="w-12 h-1.5 rounded-full bg-outline-variant overflow-hidden">
                                  <div className="h-full bg-success" style={{ width: `${Math.min(Number(row.margin), 100)}%` }} />
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 3: Randevu Raporları */}
          {activeTab === "randevu" && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {/* Top hizmetler */}
                {has("REPORT_SERVICE_REVENUE") && (
                  <RankTable title="En Çok Kazandıran Hizmetler" icon="content_cut"
                    rows={data.topServices} cols={["Hizmet", "Adet", "Ciro"]}
                    render={(s) => [s.name, fmtNum(s.count), fmtTL(s.revenue)]} />
                )}

                {/* Ortalama hizmet süreleri */}
                {has("REPORT_AVG_DURATION") && (
                  <div className="bg-surface-card rounded-2xl border border-outline-variant p-4">
                    <h3 className="text-sm font-bold text-on-surface mb-3 flex items-center gap-2">
                      <Icon name="schedule" className="!text-[18px] text-primary" /> Hizmet Başına Ortalama Süre
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-xs text-on-surface-variant border-b border-outline-variant text-left">
                            <th className="py-2 font-semibold">Hizmet</th>
                            <th className="py-2 font-semibold text-right">Ortalama Süre</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(data.avgDurations ?? []).map((row, idx) => (
                            <tr key={idx} className="border-b border-outline-variant/50 last:border-0">
                              <td className="py-2.5 text-on-surface font-medium">{row.name}</td>
                              <td className="py-2.5 text-right font-bold text-on-surface">
                                {row.avgDurationMinutes} Dakika
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              {/* No-show / iptal analizi trend */}
              {has("REPORT_NOSHOW_ANALYSIS") && (
                <div className="bg-surface-card rounded-2xl border border-outline-variant p-4">
                  <h3 className="text-sm font-bold text-on-surface mb-3">İptal / No-Show Oran Trendi (%)</h3>
                  <div className="h-[220px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={(data.noShows ?? []).map(n => ({ date: n.date, Oran: n.cancellationRate }))} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
                        <XAxis dataKey="date" tick={{ fontSize: 11, fill: C.muted }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: C.muted }} axisLine={false} tickLine={false} width={40} tickFormatter={(v) => `%${v}`} />
                        <Tooltip formatter={(v) => `%${v}`} />
                        <Line type="monotone" dataKey="Oran" stroke={C.error} strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* En Yoğun Saatler Heatmap */}
              {has("REPORT_PEAK_HOURS") && (
                <div className="bg-surface-card rounded-2xl border border-outline-variant p-4">
                  <h3 className="text-sm font-bold text-on-surface mb-3 flex items-center gap-2">
                    <Icon name="query_stats" className="!text-[18px] text-primary" /> Yoğunluk Isı Haritası (Gün / Saat)
                  </h3>
                  <div className="overflow-x-auto">
                    <div className="min-w-[640px]">
                      <div className="grid gap-1 text-center text-xs text-on-surface-variant font-semibold mb-2" style={{ gridTemplateColumns: "100px repeat(12, minmax(0, 1fr))" }}>
                        <div></div>
                        {Array.from({ length: 12 }, (_, i) => i + 9).map(hour => (
                          <div key={hour}>{hour}:00</div>
                        ))}
                      </div>
                      <div className="space-y-1">
                        {["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi", "Pazar"].map(day => {
                          const dayRows = (data.peakHours ?? []).filter(r => r.dayOfWeek === day);
                          return (
                            <div key={day} className="grid gap-1 items-center" style={{ gridTemplateColumns: "100px repeat(12, minmax(0, 1fr))" }}>
                              <div className="text-left text-xs font-semibold text-on-surface-variant truncate pr-1">{day}</div>
                              {Array.from({ length: 12 }, (_, i) => i + 9).map(hour => {
                                const row = dayRows.find(r => r.hour === hour);
                                const count = row ? Number(row.count) : 0;
                                // Background heat opacity based on count
                                const opacityClass =
                                  count === 0
                                    ? "bg-primary/0"
                                    : count <= 2
                                    ? "bg-primary/20 text-primary-dark"
                                    : count <= 5
                                    ? "bg-primary/45 text-primary-dark font-medium"
                                    : count <= 8
                                    ? "bg-primary/70 text-white font-bold"
                                    : "bg-primary text-white font-extrabold";
                                return (
                                  <div
                                    key={hour}
                                    title={`${day} saat ${hour}:00: ${count} Randevu`}
                                    className={`h-9 rounded flex items-center justify-center text-xs border border-outline-variant/35 transition hover:scale-105 cursor-pointer ${opacityClass}`}
                                  >
                                    {count > 0 ? count : ""}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 4: Müşteri Raporları */}
          {activeTab === "musteri" && (
            <div className="space-y-6">
              {/* Müşteri Retention (Tekrar Gelme Oranı) */}
              {has("REPORT_CUSTOMER_RETENTION") && data.customerRetention && (
                <div className="bg-surface-card rounded-2xl border border-outline-variant p-5 flex flex-col md:flex-row items-center gap-6">
                  <div className="relative w-36 h-36 flex items-center justify-center shrink-0">
                    {/* SVG Radial Progress */}
                    <svg className="w-full h-full transform -rotate-90">
                      <circle cx="72" cy="72" r="62" stroke="var(--md-sys-color-outline-variant, #e5e7eb)" strokeWidth="12" fill="transparent" />
                      <circle
                        cx="72"
                        cy="72"
                        r="62"
                        stroke="var(--md-sys-color-primary, #6366f1)"
                        strokeWidth="12"
                        fill="transparent"
                        strokeDasharray={2 * Math.PI * 62}
                        strokeDashoffset={2 * Math.PI * 62 * (1 - data.customerRetention.retentionRate / 100)}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute text-center">
                      <span className="text-2xl font-black text-on-surface">%{data.customerRetention.retentionRate}</span>
                      <p className="text-[10px] font-semibold text-on-surface-variant uppercase tracking-wider mt-0.5">Retention</p>
                    </div>
                  </div>
                  <div className="flex-1 space-y-3">
                    <h3 className="text-base font-bold text-on-surface">Müşteri Tekrar Gelme (Retention) Analizi</h3>
                    <p className="text-sm text-on-surface-variant">
                      Retention oranı, dönem içinde en az bir randevu alan müşterilerinizden kaçının tekrar hizmet aldığını gösterir.
                      Yüksek retention oranı, müşteri memnuniyetinin ve salon sadakatinin en önemli göstergesidir.
                    </p>
                    <div className="grid grid-cols-3 gap-3 pt-2">
                      <div className="border border-outline-variant rounded-xl p-3">
                        <span className="text-xs text-on-surface-variant">Toplam Müşteri</span>
                        <p className="text-lg font-bold text-on-surface mt-0.5">{fmtNum(data.customerRetention.totalCustomers)}</p>
                      </div>
                      <div className="border border-outline-variant rounded-xl p-3">
                        <span className="text-xs text-on-surface-variant">Dönemde Aktif</span>
                        <p className="text-lg font-bold text-on-surface mt-0.5">{fmtNum(data.customerRetention.activeInPeriod)}</p>
                      </div>
                      <div className="border border-outline-variant rounded-xl p-3">
                        <span className="text-xs text-on-surface-variant">Tekrar Gelen (Sadık)</span>
                        <p className="text-lg font-bold text-on-surface mt-0.5">{fmtNum(data.customerRetention.repeatCustomers)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Hizmet Tercihleri */}
              {has("REPORT_SERVICE_PREFERENCE") && (
                <div className="bg-surface-card rounded-2xl border border-outline-variant p-4">
                  <h3 className="text-sm font-bold text-on-surface mb-3 flex items-center gap-2">
                    <Icon name="favorite" className="!text-[18px] text-primary" /> Müşterilerin Hizmet Tercih Dağılımı
                  </h3>
                  <div className="space-y-4">
                    {(data.servicePreferences ?? []).map((row, idx) => {
                      const maxBookings = Math.max(...(data.servicePreferences ?? []).map(p => p.bookingCount), 1);
                      const pct = (row.bookingCount * 100) / maxBookings;
                      return (
                        <div key={idx} className="space-y-1.5">
                          <div className="flex justify-between text-xs font-semibold text-on-surface">
                            <span>{row.name}</span>
                            <span className="text-on-surface-variant">
                              {row.customerCount} Tekil Müşteri ({row.bookingCount} Toplam Rezervasyon)
                            </span>
                          </div>
                          <div className="w-full h-3 rounded-full bg-outline-variant overflow-hidden">
                            <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 5: Personel Raporları */}
          {activeTab === "personel" && user?.role !== "STAFF" && (
            <div className="space-y-6">
              {/* Personel Performansı */}
              {has("REPORT_STAFF_PERFORMANCE") && (
                <RankTable title="Personel Randevu ve Ciro Performansı" icon="badge"
                  rows={data.topStaff} cols={["Personel", "Randevu", "Ciro"]}
                  render={(s) => [s.name, fmtNum(s.appointments), fmtTL(s.revenue)]} />
              )}

              {/* Komisyon ve prim raporu */}
              {has("REPORT_COMMISSION") && (
                <div className="bg-surface-card rounded-2xl border border-outline-variant p-4">
                  <h3 className="text-sm font-bold text-on-surface mb-3 flex items-center gap-2">
                    <Icon name="payments" className="!text-[18px] text-primary" /> Komisyon & Prim Hakediş Raporu
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-xs text-on-surface-variant border-b border-outline-variant text-left">
                          <th className="py-2 font-semibold">Personel</th>
                          <th className="py-2 font-semibold text-right">Taban Maaş</th>
                          <th className="py-2 font-semibold text-right">Hakedilen Komisyon</th>
                          <th className="py-2 font-semibold text-right">Bonus / Prim</th>
                          <th className="py-2 font-semibold text-right">Toplam Net Ödeme</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data.staffCommissions ?? []).map((row, idx) => (
                          <tr key={idx} className="border-b border-outline-variant/50 last:border-0">
                            <td className="py-2.5 text-on-surface font-semibold">{row.name}</td>
                            <td className="py-2.5 text-right text-on-surface-variant">{fmtTL(row.baseSalary)}</td>
                            <td className="py-2.5 text-right text-on-surface">{fmtTL(row.commission)}</td>
                            <td className="py-2.5 text-right text-success font-medium">+{fmtTL(row.bonus)}</td>
                            <td className="py-2.5 text-right font-bold text-primary">{fmtTL(row.netPay)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 6: Stok Raporları */}
          {activeTab === "stok" && (
            <div className="space-y-6">
              {/* Stok Maliyet Analizi */}
              {has("REPORT_STOCK_COST") && data.stockCost && (
                <div className="bg-surface-card rounded-2xl border border-outline-variant p-4">
                  <h3 className="text-sm font-bold text-on-surface mb-3 flex items-center gap-2">
                    <Icon name="savings" className="!text-[18px] text-primary" /> Stok Maliyet Analizi
                  </h3>
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                    <StatCard label="Anlık Stok Değeri" value={data.stockCost.currentStockValue} icon="inventory_2" accent="primary" money />
                    <StatCard label="Alım Maliyeti" value={data.stockCost.purchaseCost} icon="local_shipping" accent="gold" money />
                    <StatCard label="Hizmet Tüketim Maliyeti" value={data.stockCost.consumptionCost} icon="content_cut" accent="error" money />
                    <StatCard label="Fire Maliyeti" value={data.stockCost.wasteCost} icon="delete" accent="error" money />
                    <StatCard label="Ürün Satış Cirosu" value={data.stockCost.salesRevenue} icon="storefront" accent="success" money />
                    <StatCard
                      label="Ürün Satış Kârı"
                      value={data.stockCost.salesProfit}
                      icon="trending_up"
                      accent={Number(data.stockCost.salesProfit) >= 0 ? "success" : "error"}
                      valueClass={Number(data.stockCost.salesProfit) >= 0 ? "text-success" : "text-error"}
                      money
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {/* Kritik stok */}
                {has("REPORT_CRITICAL_STOCK") && (
                  <div className="bg-surface-card rounded-2xl border border-outline-variant p-4">
                    <h3 className="text-sm font-bold text-on-surface mb-3 flex items-center gap-2">
                      <Icon name="warning" className="!text-[18px] text-error" /> Kritik Stok Seviyesindeki Ürünler
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-xs text-on-surface-variant border-b border-outline-variant text-left">
                            <th className="py-2 font-semibold">Ürün</th>
                            <th className="py-2 font-semibold text-right">Güncel Miktar</th>
                            <th className="py-2 font-semibold text-right">Kritik Eşik</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(data.criticalStocks ?? []).map((row, idx) => (
                            <tr key={idx} className="border-b border-outline-variant/50 last:border-0">
                              <td className="py-2.5 text-on-surface font-medium">{row.name}</td>
                              <td className="py-2.5 text-right text-error font-bold">
                                {row.quantity} {row.unit}
                              </td>
                              <td className="py-2.5 text-right text-on-surface-variant">
                                {row.minQuantity} {row.unit}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Ürün satış performansı */}
                {has("REPORT_PRODUCT_SALES") && (
                  <div className="bg-surface-card rounded-2xl border border-outline-variant p-4">
                    <h3 className="text-sm font-bold text-on-surface mb-3 flex items-center gap-2">
                      <Icon name="storefront" className="!text-[18px] text-primary" /> Ürün Satış Performansı
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-xs text-on-surface-variant border-b border-outline-variant text-left">
                            <th className="py-2 font-semibold">Ürün</th>
                            <th className="py-2 font-semibold text-right">Miktar</th>
                            <th className="py-2 font-semibold text-right">Ciro</th>
                            <th className="py-2 font-semibold text-right">Maliyet</th>
                            <th className="py-2 font-semibold text-right">Net Kâr</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(data.productSales ?? []).map((row, idx) => (
                            <tr key={idx} className="border-b border-outline-variant/50 last:border-0">
                              <td className="py-2.5 text-on-surface font-medium">{row.name}</td>
                              <td className="py-2.5 text-right text-on-surface-variant">{row.quantitySold} Adet</td>
                              <td className="py-2.5 text-right text-on-surface">{fmtTL(row.revenue)}</td>
                              <td className="py-2.5 text-right text-on-surface-variant">{fmtTL(row.cost)}</td>
                              <td className="py-2.5 text-right text-success font-semibold">{fmtTL(row.profit)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              {/* Hizmet Başına Ürün Tüketimi */}
              {has("REPORT_SERVICE_CONSUMPTION") && (
                <div className="bg-surface-card rounded-2xl border border-outline-variant p-4">
                  <h3 className="text-sm font-bold text-on-surface mb-3 flex items-center gap-2">
                    <Icon name="content_cut" className="!text-[18px] text-primary" /> Hizmet Başına Ürün Tüketimi
                  </h3>
                  {(data.serviceConsumption ?? []).length === 0 ? (
                    <p className="text-sm text-on-surface-variant py-4 text-center">
                      Seçili dönemde hizmette ürün kullanımı kaydedilmemiş.
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-xs text-on-surface-variant border-b border-outline-variant text-left">
                            <th className="py-2 font-semibold">Hizmet</th>
                            <th className="py-2 font-semibold">Ürün</th>
                            <th className="py-2 font-semibold text-right">Miktar</th>
                            <th className="py-2 font-semibold text-right">Maliyet</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(data.serviceConsumption ?? []).map((row, idx) => (
                            <tr key={idx} className="border-b border-outline-variant/50 last:border-0">
                              <td className="py-2.5 text-on-surface font-medium">
                                {row.serviceName || "Hizmetsiz"}
                              </td>
                              <td className="py-2.5 text-on-surface">{row.productName}</td>
                              <td className="py-2.5 text-right text-on-surface-variant">
                                {fmtNum(row.quantity)} {row.unit}
                              </td>
                              <td className="py-2.5 text-right text-error font-semibold">{fmtTL(row.cost)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === "fatura" && (
            <div className="space-y-4">
              {/* Kırılım seçici */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold text-on-surface-variant mr-1">Kırılım:</span>
                {[
                  { v: "DAY", label: "Günlük" },
                  { v: "WEEK", label: "Haftalık" },
                  { v: "MONTH", label: "Aylık" },
                  { v: "YEAR", label: "Yıllık" },
                ].map((g) => (
                  <button
                    key={g.v}
                    onClick={() => setInvGranularity(g.v)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                      invGranularity === g.v
                        ? "bg-primary text-white"
                        : "border border-outline-variant text-on-surface hover:bg-surface-container-high"
                    }`}
                  >
                    {g.label}
                  </button>
                ))}
              </div>

              {invLoading ? (
                <div className="bg-surface-card rounded-2xl border border-outline-variant p-8 text-center text-on-surface-variant">
                  Yükleniyor…
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <StatCard label="Toplam Fatura" value={invData?.totalCount ?? 0} icon="receipt_long" accent="primary" />
                    <StatCard label="Toplam Tutar" value={invData?.totalGrand ?? 0} icon="payments" accent="success" money />
                    <StatCard label="Toplam KDV" value={invData?.totalVat ?? 0} icon="account_balance" accent="gold" money />
                    <StatCard label="Toplam İndirim" value={invData?.totalDiscount ?? 0} icon="sell" accent="error" money />
                  </div>

                  {(invData?.buckets ?? []).length === 0 ? (
                    <div className="bg-surface-card rounded-2xl border border-outline-variant p-8 text-center text-on-surface-variant">
                      Seçili dönemde kesilmiş fatura bulunmuyor.
                    </div>
                  ) : (
                    <>
                      <div className="bg-surface-card rounded-2xl border border-outline-variant p-4">
                        <h3 className="text-sm font-bold text-on-surface mb-3 flex items-center gap-2">
                          <Icon name="bar_chart" className="!text-[18px] text-primary" /> Dönemsel Fatura Tutarı
                        </h3>
                        <ResponsiveContainer width="100%" height={280}>
                          <BarChart
                            data={(invData?.buckets ?? []).map((b) => ({
                              name: invBucketLabel(b.bucketDate, invData.granularity),
                              Tutar: Number(b.grandTotal || 0),
                              KDV: Number(b.vatTotal || 0),
                            }))}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
                            <XAxis dataKey="name" tick={{ fontSize: 11, fill: C.muted }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fontSize: 11, fill: C.muted }} axisLine={false} tickLine={false} />
                            <Tooltip formatter={(v) => fmtTL(v)} />
                            <Legend />
                            <Bar dataKey="Tutar" fill={C.primary} radius={[4, 4, 0, 0]} />
                            <Bar dataKey="KDV" fill={C.gold} radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>

                      <div className="bg-surface-card rounded-2xl border border-outline-variant p-4">
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-xs text-on-surface-variant border-b border-outline-variant text-left">
                                <th className="py-2 font-semibold">Dönem</th>
                                <th className="py-2 font-semibold text-right">Adet</th>
                                <th className="py-2 font-semibold text-right">Ara Toplam</th>
                                <th className="py-2 font-semibold text-right">KDV</th>
                                <th className="py-2 font-semibold text-right">İndirim</th>
                                <th className="py-2 font-semibold text-right">Genel Toplam</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(invData?.buckets ?? []).map((b, idx) => (
                                <tr key={idx} className="border-b border-outline-variant/50 last:border-0">
                                  <td className="py-2.5 text-on-surface font-medium">
                                    {invBucketLabel(b.bucketDate, invData.granularity)}
                                  </td>
                                  <td className="py-2.5 text-right text-on-surface-variant">{fmtNum(b.count)}</td>
                                  <td className="py-2.5 text-right text-on-surface-variant">{fmtTL(b.subtotal)}</td>
                                  <td className="py-2.5 text-right text-on-surface-variant">{fmtTL(b.vatTotal)}</td>
                                  <td className="py-2.5 text-right text-error">{fmtTL(b.discountTotal)}</td>
                                  <td className="py-2.5 text-right text-success font-semibold">{fmtTL(b.grandTotal)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** Otomatik haftalık/aylık rapor e-postası ayar kartı (MANAGER). */
function AutoReportEmailCard() {
  const { showToast } = useToast();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    reportService
      .getEmailSettings()
      .then((res) => setSettings(res.data?.data ?? null))
      .catch(() => {
        /* sessiz: ayar yüklenemezse kart varsayılanla açılır */
        setSettings({ enabled: false, frequency: "MONTHLY", recipientEmail: "", effectiveRecipient: "" });
      });
  }, []);

  if (!settings) return null;

  const save = async () => {
    setSaving(true);
    try {
      const res = await reportService.updateEmailSettings({
        enabled: settings.enabled,
        frequency: settings.frequency,
        recipientEmail: settings.recipientEmail || null,
      });
      setSettings(res.data?.data ?? settings);
      showToast("Otomatik rapor ayarı kaydedildi.", "success");
    } catch {
      showToast("Ayar kaydedilemedi.", "error");
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full border border-outline-variant rounded-xl px-3 py-2 outline-none focus:border-primary bg-surface-card text-on-surface";

  return (
    <div className="bg-surface-card rounded-2xl border border-outline-variant p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Icon name="forward_to_inbox" className="!text-[20px] text-primary" />
          <div>
            <h3 className="text-sm font-bold text-on-surface">Otomatik Rapor E-postası</h3>
            <p className="text-xs text-on-surface-variant">Haftalık/aylık özet raporu e-posta ile otomatik gönderilir.</p>
          </div>
        </div>
        <label className="inline-flex items-center cursor-pointer shrink-0">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={settings.enabled}
            onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
          />
          <div className="relative w-11 h-6 bg-outline-variant peer-checked:bg-primary rounded-full transition after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition peer-checked:after:translate-x-5" />
        </label>
      </div>

      {settings.enabled && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
          <div>
            <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Sıklık</label>
            <select
              value={settings.frequency}
              onChange={(e) => setSettings({ ...settings, frequency: e.target.value })}
              className={inputCls}
            >
              <option value="WEEKLY">Haftalık (her Pazartesi)</option>
              <option value="MONTHLY">Aylık (her ayın 1'i)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Alıcı e-posta (opsiyonel)</label>
            <input
              type="email"
              value={settings.recipientEmail || ""}
              onChange={(e) => setSettings({ ...settings, recipientEmail: e.target.value })}
              placeholder={settings.effectiveRecipient || "Yönetici hesabı"}
              className={inputCls}
            />
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mt-4">
        <p className="text-[11px] text-on-surface-variant/70">
          {settings.lastSentDate ? `Son gönderim: ${settings.lastSentDate}` : "Henüz gönderim yapılmadı."}
        </p>
        <button
          onClick={save}
          disabled={saving}
          className="bg-primary text-white rounded-xl px-4 py-2 font-semibold text-xs disabled:opacity-60"
        >
          {saving ? "Kaydediliyor…" : "Kaydet"}
        </button>
      </div>
    </div>
  );
}

function UpsellBanner() {
  return (
    <div className="bg-[#fbbf24]/10 border border-[#fbbf24]/30 rounded-2xl p-6 flex items-start gap-4 print:hidden">
      <div className="w-12 h-12 rounded-2xl bg-[#fbbf24]/20 text-[#b45309] flex items-center justify-center shrink-0">
        <Icon name="workspace_premium" filled className="!text-[26px]" />
      </div>
      <div>
        <h4 className="font-bold text-on-surface text-base">Detaylı raporlar Gold pakete özeldir</h4>
        <p className="text-sm text-on-surface-variant mt-1">
          Aylık gelir/gider trendi, en çok kazandıran hizmetler, personel performansı ve
          kategori kırılımları için paketinizi <b>Gold</b>'a yükseltin.
        </p>
      </div>
    </div>
  );
}

const CATEGORY_ICON = {
  Finans: "payments",
  Randevu: "event",
  Müşteri: "group",
  Personel: "badge",
  Stok: "inventory_2",
  Paket: "workspace_premium",
  Fatura: "receipt_long",
  Genel: "insights",
};

// Verisi henüz olmayan rapor için yer tutucu kart.
function PlannedCard({ feature }) {
  return (
    <div className="relative bg-surface-card rounded-2xl border border-dashed border-outline-variant p-4 opacity-90">
      <span className="absolute top-3 right-3 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-primary/10 text-primary">
        Diğer
      </span>
      <div className="w-10 h-10 rounded-xl bg-surface-container-high text-on-surface-variant flex items-center justify-center mb-3">
        <Icon name={CATEGORY_ICON[feature.category] ?? "assessment"} />
      </div>
      <p className="font-semibold text-on-surface text-sm pr-14">{feature.label}</p>
      {feature.description && (
        <p className="text-xs text-on-surface-variant mt-1">{feature.description}</p>
      )}
      <p className="text-[11px] text-on-surface-variant/70 mt-2">{feature.category} raporu</p>
    </div>
  );
}

function RankTable({ title, icon, rows = [], cols, render }) {
  return (
    <div className="bg-surface-card rounded-2xl border border-outline-variant p-4">
      <h3 className="text-sm font-bold text-on-surface mb-3 flex items-center gap-2">
        <Icon name={icon} className="!text-[18px] text-primary" /> {title}
      </h3>
      {rows.length === 0 ? (
        <div className="py-8 text-center text-sm text-on-surface-variant">Veri yok</div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-on-surface-variant border-b border-outline-variant">
              <th className="text-left font-semibold py-1.5">{cols[0]}</th>
              <th className="text-right font-semibold py-1.5">{cols[1]}</th>
              <th className="text-right font-semibold py-1.5">{cols[2]}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const c = render(r);
              return (
                <tr key={i} className="border-b border-outline-variant/50 last:border-0">
                  <td className="py-2 text-on-surface truncate">{c[0]}</td>
                  <td className="py-2 text-right text-on-surface-variant">{c[1]}</td>
                  <td className="py-2 text-right font-semibold text-on-surface">{c[2]}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function CategoryCard({ title, rows = [] }) {
  const PIE = useChartColors().pie;
  const pieData = rows.map((r) => ({ name: r.category, value: Number(r.amount || 0) }));
  return (
    <div className="bg-surface-card rounded-2xl border border-outline-variant p-4">
      <h3 className="text-sm font-bold text-on-surface mb-3">{title}</h3>
      {pieData.length === 0 ? (
        <div className="h-[200px] flex items-center justify-center text-sm text-on-surface-variant">Veri yok</div>
      ) : (
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <ResponsiveContainer width="100%" height={200} className="max-w-[220px]">
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2}>
                {pieData.map((d, i) => (
                  <Cell key={d.name} fill={PIE[i % PIE.length]} stroke="none" />
                ))}
              </Pie>
              <Tooltip formatter={(v) => fmtTL(v)} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex-1 w-full space-y-1.5">
            {pieData.map((d, i) => (
              <div key={d.name} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-1.5 text-on-surface-variant truncate">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: PIE[i % PIE.length] }} />
                  {d.name}
                </span>
                <span className="font-semibold text-on-surface">{fmtTL(d.value)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------- ADMIN */

function PlatformReport() {
  const { showToast } = useToast();
  const PIE = useChartColors().pie;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    reportService
      .platform()
      .then((res) => setData(res.data?.data ?? null))
      .catch(() => showToast("Platform raporu yüklenemedi.", "error"))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pkgData = useMemo(
    () => (data?.byPackage ?? []).map((p) => ({ name: p.packageName, value: Number(p.salonCount || 0) })),
    [data]
  );

  const exportCsv = () => {
    if (!data) return;
    const rows = [
      ["Platform Raporu"],
      [],
      ["Metrik", "Değer"],
      ["Toplam Salon", data.totalSalons],
      ["Aktif Salon", data.activeSalons],
      ["Pasif Salon", data.inactiveSalons],
      ["Askıda Salon", data.suspendedSalons],
      ["Tahmini Aylık Gelir (MRR)", data.estimatedMonthlyRevenue],
      ["Toplam Müşteri", data.totalCustomers],
      ["Toplam Personel", data.totalStaff],
      ["Toplam Randevu", data.totalAppointments],
      [],
      ["Paket", "Seviye", "Salon Sayısı", "Aylık Ücret"],
      ...(data.byPackage ?? []).map((p) => [p.packageName, p.level, p.salonCount, p.monthlyPrice]),
    ];
    downloadCsv("platform_raporu.csv", rows);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4 print:hidden">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
            <Icon name="analytics" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-on-surface">Yönetici Paneli</h1>
            <p className="text-on-surface-variant text-sm">Platform geneli istatistikler ve paket dağılımı.</p>
          </div>
        </div>
        <div className="flex items-end gap-2">
          <button onClick={exportCsv} disabled={!data}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium bg-primary text-white hover:opacity-90 transition disabled:opacity-40">
            <Icon name="download" className="!text-[18px]" /> CSV
          </button>
          <button onClick={() => window.print()} disabled={!data}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium border border-outline-variant text-on-surface hover:bg-surface-container-high transition disabled:opacity-40">
            <Icon name="print" className="!text-[18px]" /> Yazdır
          </button>
        </div>
      </div>

      {loading ? (
        <div className="bg-surface-card rounded-2xl p-10 border border-outline-variant text-center text-on-surface-variant">Yükleniyor…</div>
      ) : !data ? (
        <div className="bg-surface-card rounded-2xl p-10 border border-outline-variant text-center text-on-surface-variant">Veri yok.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard label="Toplam Salon" value={data.totalSalons} icon="storefront" />
            <StatCard label="Aktif" value={data.activeSalons} icon="check_circle" accent="success" valueClass="text-success" />
            <StatCard label="Askıda" value={data.suspendedSalons} icon="pause_circle" accent="error" valueClass="text-error" />
            <StatCard money label="Aylık Gelir (MRR)" value={data.estimatedMonthlyRevenue} icon="payments" accent="gold" valueClass="text-[#b45309]" />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard label="Pasif Salon" value={data.inactiveSalons} icon="cancel" />
            <StatCard label="Toplam Müşteri" value={data.totalCustomers} icon="group" />
            <StatCard label="Toplam Personel" value={data.totalStaff} icon="badge" />
            <StatCard label="Toplam Randevu" value={data.totalAppointments} icon="event" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {/* Paket dağılımı grafiği */}
            <div className="bg-surface-card rounded-2xl border border-outline-variant p-4">
              <h3 className="text-sm font-bold text-on-surface mb-3">Paket Dağılımı</h3>
              {pkgData.length === 0 ? (
                <div className="h-[220px] flex items-center justify-center text-sm text-on-surface-variant">Veri yok</div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={pkgData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={2}>
                      {pkgData.map((d, i) => (
                        <Cell key={d.name} fill={PIE[i % PIE.length]} stroke="none" />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => `${v} salon`} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Paket tablosu */}
            <div className="bg-surface-card rounded-2xl border border-outline-variant p-4">
              <h3 className="text-sm font-bold text-on-surface mb-3">Paket Detayı</h3>
              {(data.byPackage ?? []).length === 0 ? (
                <div className="py-8 text-center text-sm text-on-surface-variant">Veri yok</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-on-surface-variant border-b border-outline-variant">
                      <th className="text-left font-semibold py-1.5">Paket</th>
                      <th className="text-right font-semibold py-1.5">Salon</th>
                      <th className="text-right font-semibold py-1.5">Aylık Ücret</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byPackage.map((p) => (
                      <tr key={p.packageName} className="border-b border-outline-variant/50 last:border-0">
                        <td className="py-2 text-on-surface">{p.packageName}</td>
                        <td className="py-2 text-right text-on-surface-variant">{fmtNum(p.salonCount)}</td>
                        <td className="py-2 text-right font-semibold text-on-surface">{fmtTL(p.monthlyPrice)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
